/**
 * HYPSEYA — Integration Studio
 * Full psychedelic arc: Preparation (T−14 → T0) → Ceremony → Integration (T+1 → T+30).
 * Includes safety screening, MEQ-30 + EBI acute-experience assessment, and SOS support.
 * Used as a tab inside TransformationSuite.
 */
import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PHASE_COLORS = [
  { bg: 'rgba(139,115,85,0.1)',  border: 'rgba(139,115,85,0.25)',  accent: '#8B7355' },
  { bg: 'rgba(107,84,139,0.1)', border: 'rgba(107,84,139,0.25)', accent: '#6B548B' },
  { bg: 'rgba(30,107,104,0.1)', border: 'rgba(30,107,104,0.25)', accent: '#1E6B68' },
  { bg: 'rgba(45,139,135,0.1)', border: 'rgba(45,139,135,0.25)', accent: '#2D8B87' },
  { bg: 'rgba(15,75,73,0.12)',  border: 'rgba(15,75,73,0.3)',    accent: '#0F4B49' },
];

const CEREMONY_TYPES = [
  'Psilocybin', 'MDMA', 'Ayahuasca', 'Ketamine', 'LSD',
  '5-MeO-DMT', 'Kambo', 'Breathwork', 'Other',
];

const SCREENING_QUESTIONS = [
  { key: 'personal_psychosis_history', text: 'Have you ever been diagnosed with psychosis or schizophrenia?' },
  { key: 'family_psychosis_history', text: 'Has a first-degree relative (parent or sibling) been diagnosed with psychosis or schizophrenia?' },
  { key: 'bipolar_diagnosis', text: 'Have you been diagnosed with bipolar disorder?' },
  { key: 'recent_suicidal_ideation', text: 'In the past month, have you had thoughts of harming yourself?' },
  { key: 'med_ssri', text: 'Are you currently taking an SSRI antidepressant?' },
  { key: 'med_snri', text: 'Are you currently taking an SNRI antidepressant?' },
  { key: 'med_maoi', text: 'Are you currently taking an MAOI?' },
  { key: 'med_lithium', text: 'Are you currently taking lithium?' },
  { key: 'med_tramadol', text: 'Are you currently taking tramadol?' },
  { key: 'med_antipsychotics', text: 'Are you currently taking antipsychotic medication?' },
  { key: 'heart_condition', text: 'Do you have a diagnosed heart condition?' },
  { key: 'uncontrolled_hypertension', text: 'Do you have uncontrolled high blood pressure?' },
  { key: 'seizure_history', text: 'Have you ever had a seizure?' },
  { key: 'pregnancy', text: 'Are you pregnant or breastfeeding?' },
];

const card = {
  background: 'rgba(255,255,255,0.95)', borderRadius: 16,
  border: '1px solid rgba(184,211,207,0.3)', padding: '20px', marginBottom: 14,
};
const sectionLabel = {
  margin: '0 0 10px', fontSize: '0.72rem', fontWeight: 700, color: '#AAB8B6',
  textTransform: 'uppercase', letterSpacing: '0.08em',
};
const primaryBtn = (disabled) => ({
  width: '100%', padding: '14px',
  background: disabled ? 'rgba(30,107,104,0.3)' : 'linear-gradient(135deg, #1E6B68 0%, #2D8B87 100%)',
  color: '#fff', border: 'none', borderRadius: 12,
  fontSize: '0.95rem', fontWeight: 600, cursor: disabled ? 'not-allowed' : 'pointer',
});

function MoodDots({ value, onChange }) {
  return (
    <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
      {[1, 2, 3, 4, 5].map(n => (
        <button
          key={n}
          onClick={() => onChange(n)}
          style={{
            width: 32, height: 32, borderRadius: '50%', border: 'none', cursor: 'pointer',
            background: n <= value ? '#1E6B68' : 'rgba(30,107,104,0.1)',
            transition: 'all 0.15s ease',
            transform: n === value ? 'scale(1.15)' : 'scale(1)',
          }}
        />
      ))}
      <span style={{ fontSize: '0.78rem', color: '#8FA3A0', marginLeft: 4 }}>
        {['', 'Difficult', 'Unsettled', 'Neutral', 'Open', 'Grounded'][value]}
      </span>
    </div>
  );
}

function PhaseBar({ phaseIndex, dayNumber }) {
  const phases = ['Landing', 'Unfolding', 'Digestion', 'Integration', 'Anchoring'];
  const phaseDays = [[1,3],[4,7],[8,14],[15,21],[22,30]];
  return (
    <div style={{ display: 'flex', gap: 4, marginBottom: 20 }}>
      {phases.map((name, i) => {
        const [start, end] = phaseDays[i];
        const isActive = i === phaseIndex;
        const isPast   = i < phaseIndex;
        const progress = isActive
          ? Math.min(1, (dayNumber - start) / (end - start + 1))
          : isPast ? 1 : 0;
        return (
          <div key={name} style={{ flex: 1 }}>
            <div style={{
              height: 4, borderRadius: 4,
              background: 'rgba(30,107,104,0.1)',
              overflow: 'hidden', marginBottom: 4,
            }}>
              <div style={{
                height: '100%',
                width: `${progress * 100}%`,
                background: isActive ? '#1E6B68' : isPast ? '#2D8B87' : 'transparent',
                transition: 'width 0.4s ease',
              }} />
            </div>
            <span style={{
              fontSize: '0.6rem', color: isActive ? '#1E6B68' : isPast ? '#2D8B87' : '#B8C8C6',
              fontWeight: isActive ? 700 : 500, display: 'block', textAlign: 'center',
            }}>{name}</span>
          </div>
        );
      })}
    </div>
  );
}

// ── SOS Overlay — difficult moment support ──────────────────────────────────
function SOSOverlay({ userId, protocolId, onClose }) {
  const [phase, setPhase] = useState('breathe'); // breathe | support
  const [breathLabel, setBreathLabel] = useState('Breathe in');
  const [note, setNote] = useState('');
  const [notify, setNotify] = useState(false);
  const [sending, setSending] = useState(false);
  const [logged, setLogged] = useState(false);

  // 4-7-8 breathing cycle labels
  useEffect(() => {
    if (phase !== 'breathe') return;
    let cancelled = false;
    const cycle = async () => {
      while (!cancelled) {
        setBreathLabel('Breathe in — 4');
        await new Promise(r => setTimeout(r, 4000));
        if (cancelled) break;
        setBreathLabel('Hold — 7');
        await new Promise(r => setTimeout(r, 7000));
        if (cancelled) break;
        setBreathLabel('Out slowly — 8');
        await new Promise(r => setTimeout(r, 8000));
      }
    };
    cycle();
    return () => { cancelled = true; };
  }, [phase]);

  const handleLog = async () => {
    setSending(true);
    try {
      const res = await axios.post(`${API}/integration/sos`, {
        user_id: userId,
        protocol_id: protocolId,
        note: note.trim(),
        alert_practitioner: notify,
      });
      setLogged(true);
      if (res.data?.alerted_practitioner) {
        toast.success('Your practitioner has been notified');
      } else if (notify) {
        toast.info('No connected practitioner found — moment logged for you');
      } else {
        toast.success('Moment logged. Be gentle with yourself.');
      }
    } catch {
      toast.error('Could not log this moment — but the practice still counts.');
    } finally {
      setSending(false);
    }
  };

  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 1000,
      background: 'linear-gradient(160deg, #0D2B2A 0%, #12211F 100%)',
      overflowY: 'auto', padding: '40px 20px 60px',
    }}>
      <div style={{ maxWidth: 480, margin: '0 auto' }}>
        <button
          onClick={onClose}
          style={{
            background: 'rgba(255,255,255,0.08)', border: 'none', color: 'rgba(255,255,255,0.7)',
            borderRadius: 100, padding: '8px 18px', fontSize: '0.82rem', cursor: 'pointer', marginBottom: 28,
          }}
        >
          ← Back
        </button>

        {phase === 'breathe' && (
          <div style={{ textAlign: 'center' }}>
            <p style={{ color: 'rgba(255,255,255,0.55)', fontSize: '0.85rem', margin: '0 0 6px' }}>
              You are safe. This is a wave — waves pass.
            </p>
            <h2 style={{
              color: '#fff', fontFamily: "'Playfair Display', serif",
              fontWeight: 400, fontSize: '1.35rem', margin: '0 0 40px',
            }}>
              Breathe with the circle
            </h2>
            <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 24 }}>
              <div style={{
                width: 160, height: 160, borderRadius: '50%',
                border: '1.5px solid rgba(184,211,207,0.4)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                animation: 'sosBreath 19s ease-in-out infinite',
              }}>
                <div style={{
                  width: 110, height: 110, borderRadius: '50%',
                  background: 'radial-gradient(circle, rgba(45,139,135,0.5) 0%, rgba(30,107,104,0.15) 70%)',
                }} />
              </div>
            </div>
            <style>{`@keyframes sosBreath {
              0% { transform: scale(0.85); } 21% { transform: scale(1.1); }
              58% { transform: scale(1.1); } 100% { transform: scale(0.85); }
            }`}</style>
            <p style={{ color: '#B8D3CF', fontSize: '1.05rem', fontWeight: 600, minHeight: 28 }}>
              {breathLabel}
            </p>
            <p style={{ color: 'rgba(255,255,255,0.35)', fontSize: '0.78rem', margin: '20px 0 36px', lineHeight: 1.6 }}>
              Stay here as long as you need.<br />Feet on the floor. Jaw loose. Nothing to solve right now.
            </p>
            <button
              onClick={() => setPhase('support')}
              style={{
                background: 'rgba(255,255,255,0.1)', border: '1px solid rgba(255,255,255,0.2)',
                color: '#fff', borderRadius: 12, padding: '13px 28px',
                fontSize: '0.9rem', fontWeight: 600, cursor: 'pointer',
              }}
            >
              I feel a bit steadier →
            </button>
          </div>
        )}

        {phase === 'support' && (
          <div>
            <h2 style={{
              color: '#fff', fontFamily: "'Playfair Display', serif",
              fontWeight: 400, fontSize: '1.3rem', margin: '0 0 18px',
            }}>
              What came up is part of the process
            </h2>
            <div style={{
              background: 'rgba(255,255,255,0.06)', borderRadius: 14, padding: '16px 18px', marginBottom: 14,
            }}>
              <p style={{ margin: '0 0 10px', fontSize: '0.72rem', fontWeight: 700, color: 'rgba(184,211,207,0.6)', textTransform: 'uppercase', letterSpacing: '0.08em' }}>
                Reach out for professional help if
              </p>
              {[
                'Thoughts of harming yourself or others appear',
                'You feel disconnected from reality for hours, not moments',
                'Panic does not soften at all after grounding',
                'You cannot sleep for more than two nights',
              ].map((t, i) => (
                <p key={i} style={{ margin: '0 0 7px', fontSize: '0.82rem', color: 'rgba(255,255,255,0.7)', lineHeight: 1.5 }}>
                  · {t}
                </p>
              ))}
              <p style={{ margin: '10px 0 0', fontSize: '0.78rem', color: 'rgba(255,255,255,0.45)' }}>
                In an emergency, contact local emergency services immediately.
              </p>
            </div>

            {!logged ? (
              <div style={{
                background: 'rgba(255,255,255,0.06)', borderRadius: 14, padding: '16px 18px',
              }}>
                <p style={{ margin: '0 0 10px', fontSize: '0.72rem', fontWeight: 700, color: 'rgba(184,211,207,0.6)', textTransform: 'uppercase', letterSpacing: '0.08em' }}>
                  Log this moment (optional)
                </p>
                <textarea
                  value={note}
                  onChange={e => setNote(e.target.value)}
                  placeholder="A few words about what came up…"
                  rows={3}
                  style={{
                    width: '100%', padding: '10px 12px', boxSizing: 'border-box',
                    border: '1px solid rgba(255,255,255,0.15)', borderRadius: 10,
                    fontSize: '0.85rem', color: '#fff', background: 'rgba(0,0,0,0.2)',
                    outline: 'none', resize: 'none', lineHeight: 1.6, fontFamily: 'inherit',
                  }}
                />
                <label style={{ display: 'flex', gap: 10, alignItems: 'center', margin: '12px 0 16px', cursor: 'pointer' }}>
                  <input type="checkbox" checked={notify} onChange={e => setNotify(e.target.checked)} />
                  <span style={{ fontSize: '0.82rem', color: 'rgba(255,255,255,0.7)' }}>
                    Notify my therapist / practitioner
                  </span>
                </label>
                <button onClick={handleLog} disabled={sending} style={primaryBtn(sending)}>
                  {sending ? 'Saving…' : 'Log this moment'}
                </button>
              </div>
            ) : (
              <div style={{ textAlign: 'center', padding: '20px 0' }}>
                <p style={{ color: '#B8D3CF', fontSize: '0.9rem', marginBottom: 20 }}>
                  Logged. This moment is now part of your integration record.
                </p>
                <button onClick={onClose} style={{ ...primaryBtn(false), width: 'auto', padding: '13px 32px' }}>
                  Return to the studio
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// ── MEQ-30 + EBI — acute experience assessment ──────────────────────────────
function AcuteAssessment({ userId, protocolId, onDone, onClose }) {
  const [step, setStep] = useState('intro'); // intro | meq | ebi | results
  const [meqItems, setMeqItems] = useState([]);
  const [meqAnchors, setMeqAnchors] = useState([]);
  const [ebiItems, setEbiItems] = useState([]);
  const [meqAnswers, setMeqAnswers] = useState({});
  const [ebiAnswers, setEbiAnswers] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [results, setResults] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const [meq, ebi] = await Promise.all([
          axios.get(`${API}/assessments/meq30/items`),
          axios.get(`${API}/assessments/ebi/items`),
        ]);
        setMeqItems(meq.data.items || []);
        setMeqAnchors(meq.data.anchors || []);
        setEbiItems(ebi.data.items || []);
      } catch {
        toast.error('Could not load the questionnaires');
      }
    })();
  }, []);

  const meqComplete = meqItems.length > 0 && meqItems.every(it => meqAnswers[it.index] !== undefined);
  const ebiComplete = ebiItems.length > 0 && ebiItems.every(it => ebiAnswers[it.index] !== undefined);

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      const meqPayload = meqItems.map(it => meqAnswers[it.index]);
      const ebiPayload = ebiItems.map(it => ebiAnswers[it.index]);
      const [meqRes, ebiRes] = await Promise.all([
        axios.post(`${API}/assessments/meq30`, { user_id: userId, protocol_id: protocolId, answers: meqPayload }),
        axios.post(`${API}/assessments/ebi`, { user_id: userId, protocol_id: protocolId, answers: ebiPayload }),
      ]);
      setResults({ meq: meqRes.data, ebi: ebiRes.data });
      setStep('results');
      onDone();
    } catch {
      toast.error('Could not save the assessment');
    } finally {
      setSubmitting(false);
    }
  };

  const subscaleNames = { M: 'Mystical', P: 'Positive mood', T: 'Time & space', I: 'Ineffability' };

  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 1000, background: '#F4F9F8',
      overflowY: 'auto', padding: '32px 20px 80px',
    }}>
      <div style={{ maxWidth: 560, margin: '0 auto' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
          <button
            onClick={onClose}
            style={{
              background: 'rgba(30,107,104,0.08)', border: 'none', color: '#1E6B68',
              borderRadius: 100, padding: '8px 18px', fontSize: '0.82rem', cursor: 'pointer',
            }}
          >
            ← Back
          </button>
          {step === 'meq' && (
            <span style={{ fontSize: '0.78rem', color: '#8FA3A0' }}>
              {Object.keys(meqAnswers).length} / {meqItems.length}
            </span>
          )}
        </div>

        {step === 'intro' && (
          <div>
            <h2 style={{ fontFamily: "'Playfair Display', serif", fontWeight: 500, fontSize: '1.5rem', color: '#1E3A39', margin: '0 0 14px' }}>
              Acute Experience Assessment
            </h2>
            <p style={{ fontSize: '0.9rem', color: '#5A7370', lineHeight: 1.7, margin: '0 0 14px' }}>
              Two validated research scales about the ceremony itself — the same instruments used
              by leading psychedelic research centres.
            </p>
            <div style={card}>
              <p style={{ margin: '0 0 6px', fontWeight: 700, fontSize: '0.85rem', color: '#1E3A39' }}>MEQ-30</p>
              <p style={{ margin: 0, fontSize: '0.82rem', color: '#8FA3A0', lineHeight: 1.6 }}>
                Mystical Experience Questionnaire — 30 items about the qualities of your experience.
                Higher scores are among the strongest predictors of lasting benefit.
              </p>
            </div>
            <div style={card}>
              <p style={{ margin: '0 0 6px', fontWeight: 700, fontSize: '0.85rem', color: '#1E3A39' }}>EBI</p>
              <p style={{ margin: 0, fontSize: '0.82rem', color: '#8FA3A0', lineHeight: 1.6 }}>
                Emotional Breakthrough Inventory — 6 items on emotional release and resolution during the session.
              </p>
            </div>
            <p style={{ fontSize: '0.78rem', color: '#AAB8B6', margin: '4px 0 18px' }}>
              Takes about 8 minutes. Answer for the ceremony as a whole, not how you feel now.
            </p>
            <button onClick={() => setStep('meq')} style={primaryBtn(meqItems.length === 0)} disabled={meqItems.length === 0}>
              Begin →
            </button>
          </div>
        )}

        {step === 'meq' && (
          <div>
            <h3 style={{ fontFamily: "'Playfair Display', serif", fontWeight: 500, fontSize: '1.2rem', color: '#1E3A39', margin: '0 0 6px' }}>
              MEQ-30
            </h3>
            <p style={{ fontSize: '0.8rem', color: '#8FA3A0', margin: '0 0 18px', lineHeight: 1.6 }}>
              Looking back on the entirety of your session, rate the degree to which — at any time —
              you experienced each phenomenon. 0 = none · 5 = extreme (more than any other time in your life).
            </p>
            {meqItems.map(it => (
              <div key={it.index} style={{ ...card, padding: '14px 16px', marginBottom: 10 }}>
                <p style={{ margin: '0 0 10px', fontSize: '0.85rem', color: '#1E3A39', lineHeight: 1.55 }}>
                  <span style={{ color: '#AAB8B6', fontWeight: 700, marginRight: 6 }}>{it.index + 1}.</span>
                  {it.text}
                </p>
                <div style={{ display: 'flex', gap: 6 }}>
                  {[0, 1, 2, 3, 4, 5].map(v => (
                    <button
                      key={v}
                      onClick={() => setMeqAnswers(a => ({ ...a, [it.index]: v }))}
                      title={meqAnchors[v] || ''}
                      style={{
                        flex: 1, padding: '9px 0', borderRadius: 8, cursor: 'pointer',
                        border: meqAnswers[it.index] === v ? 'none' : '1px solid rgba(30,107,104,0.18)',
                        background: meqAnswers[it.index] === v ? '#1E6B68' : '#fff',
                        color: meqAnswers[it.index] === v ? '#fff' : '#5A7370',
                        fontSize: '0.85rem', fontWeight: 600,
                      }}
                    >
                      {v}
                    </button>
                  ))}
                </div>
              </div>
            ))}
            <button onClick={() => setStep('ebi')} style={{ ...primaryBtn(!meqComplete), marginTop: 8 }} disabled={!meqComplete}>
              {meqComplete ? 'Continue to EBI →' : `Answer all items (${Object.keys(meqAnswers).length}/${meqItems.length})`}
            </button>
          </div>
        )}

        {step === 'ebi' && (
          <div>
            <h3 style={{ fontFamily: "'Playfair Display', serif", fontWeight: 500, fontSize: '1.2rem', color: '#1E3A39', margin: '0 0 6px' }}>
              Emotional Breakthrough Inventory
            </h3>
            <p style={{ fontSize: '0.8rem', color: '#8FA3A0', margin: '0 0 18px', lineHeight: 1.6 }}>
              For each statement, rate from 0 (not at all) to 100 (entirely) how much it describes your session.
            </p>
            {ebiItems.map(it => (
              <div key={it.index} style={{ ...card, padding: '16px', marginBottom: 10 }}>
                <p style={{ margin: '0 0 12px', fontSize: '0.85rem', color: '#1E3A39', lineHeight: 1.55 }}>{it.text}</p>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                  <input
                    type="range" min={0} max={100}
                    value={ebiAnswers[it.index] ?? 0}
                    onChange={e => setEbiAnswers(a => ({ ...a, [it.index]: Number(e.target.value) }))}
                    style={{ flex: 1, accentColor: '#1E6B68' }}
                  />
                  <span style={{
                    minWidth: 36, textAlign: 'right', fontSize: '0.9rem', fontWeight: 700,
                    color: ebiAnswers[it.index] !== undefined ? '#1E6B68' : '#AAB8B6',
                  }}>
                    {ebiAnswers[it.index] ?? '—'}
                  </span>
                </div>
              </div>
            ))}
            <button onClick={handleSubmit} style={{ ...primaryBtn(!ebiComplete || submitting), marginTop: 8 }} disabled={!ebiComplete || submitting}>
              {submitting ? 'Saving…' : ebiComplete ? 'Submit assessment' : 'Move each slider to answer'}
            </button>
          </div>
        )}

        {step === 'results' && results && (
          <div>
            <h3 style={{ fontFamily: "'Playfair Display', serif", fontWeight: 500, fontSize: '1.3rem', color: '#1E3A39', margin: '0 0 16px' }}>
              Your experience profile
            </h3>
            <div style={card}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 14 }}>
                <p style={{ margin: 0, fontWeight: 700, fontSize: '0.85rem', color: '#1E3A39' }}>MEQ-30</p>
                <span style={{ fontSize: '1.3rem', fontWeight: 700, color: '#1E6B68' }}>{results.meq.total_pct}%</span>
              </div>
              {Object.entries(results.meq.subscale_pct).map(([k, v]) => (
                <div key={k} style={{ marginBottom: 10 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                    <span style={{ fontSize: '0.78rem', color: '#5A7370' }}>{subscaleNames[k]}</span>
                    <span style={{ fontSize: '0.78rem', fontWeight: 700, color: v >= 60 ? '#1E6B68' : '#8FA3A0' }}>{v}%</span>
                  </div>
                  <div style={{ height: 5, borderRadius: 4, background: 'rgba(30,107,104,0.1)', overflow: 'hidden' }}>
                    <div style={{ height: '100%', width: `${v}%`, background: v >= 60 ? '#1E6B68' : '#8FADA9', borderRadius: 4 }} />
                  </div>
                </div>
              ))}
              {results.meq.complete_mystical_experience && (
                <p style={{
                  margin: '14px 0 0', padding: '10px 14px', borderRadius: 10,
                  background: 'rgba(30,107,104,0.08)', fontSize: '0.8rem', color: '#1E6B68', fontWeight: 600,
                }}>
                  ◈ Complete mystical experience (≥60% on all four subscales) — associated in research
                  with the strongest long-term outcomes.
                </p>
              )}
            </div>
            <div style={card}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
                <p style={{ margin: 0, fontWeight: 700, fontSize: '0.85rem', color: '#1E3A39' }}>Emotional Breakthrough</p>
                <span style={{ fontSize: '1.3rem', fontWeight: 700, color: '#1E6B68' }}>{results.ebi.ebi_score}</span>
              </div>
              <p style={{ margin: '6px 0 0', fontSize: '0.78rem', color: '#8FA3A0' }}>0–100 scale · mean of 6 items</p>
            </div>
            <p style={{ fontSize: '0.78rem', color: '#AAB8B6', lineHeight: 1.6, margin: '4px 0 18px' }}>
              These scores describe the experience — they are not a clinical evaluation of you.
              They become part of your integration record and can inform your work with a professional.
            </p>
            <button onClick={onClose} style={primaryBtn(false)}>
              Continue integration →
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

// ── Safety Screening form (preparation phase) ───────────────────────────────
function ScreeningForm({ userId, protocolId, onSaved }) {
  const [answers, setAnswers] = useState({});
  const [saving, setSaving] = useState(false);
  const [result, setResult] = useState(null);

  const allAnswered = SCREENING_QUESTIONS.every(q => answers[q.key] !== undefined);

  const handleSubmit = async () => {
    setSaving(true);
    try {
      const res = await axios.post(`${API}/integration/screening`, {
        user_id: userId, protocol_id: protocolId, answers,
      });
      setResult(res.data);
      onSaved();
    } catch {
      toast.error('Could not save screening');
    } finally {
      setSaving(false);
    }
  };

  if (result) {
    return (
      <div>
        {result.flags.length === 0 ? (
          <p style={{ margin: 0, fontSize: '0.85rem', color: '#1E6B68', fontWeight: 600 }}>
            ✓ No flags raised. Share this screening with your facilitator all the same.
          </p>
        ) : (
          <div>
            <p style={{ margin: '0 0 10px', fontSize: '0.82rem', color: '#1E3A39', fontWeight: 700 }}>
              {result.flags.length} item{result.flags.length > 1 ? 's' : ''} to review with a professional:
            </p>
            {result.flags.map((f, i) => (
              <div key={i} style={{
                padding: '10px 14px', borderRadius: 10, marginBottom: 8,
                background: f.severity === 'critical' ? 'rgba(180,60,60,0.08)' : 'rgba(201,152,62,0.08)',
                border: `1px solid ${f.severity === 'critical' ? 'rgba(180,60,60,0.25)' : 'rgba(201,152,62,0.25)'}`,
              }}>
                <p style={{ margin: 0, fontSize: '0.8rem', lineHeight: 1.55, color: '#1E3A39' }}>
                  <strong style={{ color: f.severity === 'critical' ? '#A84343' : '#B08430', textTransform: 'uppercase', fontSize: '0.68rem', letterSpacing: '0.06em' }}>
                    {f.severity}
                  </strong>
                  {' — '}{f.message}
                </p>
              </div>
            ))}
            <p style={{ margin: '10px 0 0', fontSize: '0.75rem', color: '#8FA3A0', lineHeight: 1.6 }}>
              This is decision-support for you and your facilitator — not a medical decision.
              Take these flags to a prescriber or physician before the session.
            </p>
          </div>
        )}
      </div>
    );
  }

  return (
    <div>
      {SCREENING_QUESTIONS.map(q => (
        <div key={q.key} style={{
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          gap: 12, padding: '10px 0', borderBottom: '1px solid rgba(184,211,207,0.25)',
        }}>
          <p style={{ margin: 0, fontSize: '0.82rem', color: '#1E3A39', lineHeight: 1.5, flex: 1 }}>{q.text}</p>
          <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
            {[{ v: false, label: 'No' }, { v: true, label: 'Yes' }].map(opt => (
              <button
                key={opt.label}
                onClick={() => setAnswers(a => ({ ...a, [q.key]: opt.v }))}
                style={{
                  padding: '7px 16px', borderRadius: 100, cursor: 'pointer', fontSize: '0.78rem', fontWeight: 600,
                  border: answers[q.key] === opt.v ? 'none' : '1px solid rgba(30,107,104,0.2)',
                  background: answers[q.key] === opt.v ? (opt.v ? '#B08430' : '#1E6B68') : '#fff',
                  color: answers[q.key] === opt.v ? '#fff' : '#5A7370',
                }}
              >
                {opt.label}
              </button>
            ))}
          </div>
        </div>
      ))}
      <button
        onClick={handleSubmit}
        disabled={!allAnswered || saving}
        style={{ ...primaryBtn(!allAnswered || saving), marginTop: 16 }}
      >
        {saving ? 'Saving…' : allAnswered ? 'Submit screening' : `Answer all questions (${Object.keys(answers).length}/${SCREENING_QUESTIONS.length})`}
      </button>
    </div>
  );
}

// ── Active Preparation Screen (T−14 → T0) ───────────────────────────────────
function ActivePreparation({ userId, data, onRefresh }) {
  const navigate = useNavigate();
  const { protocol, days_until_ceremony, prep_day, daily_prompt, today_done, today_entry,
          screening_done, screening, baseline_done, phase } = data;

  const [text, setText] = useState(today_entry?.text || '');
  const [saving, setSaving] = useState(false);
  const [showScreening, setShowScreening] = useState(false);
  const [startingIntegration, setStartingIntegration] = useState(false);

  useEffect(() => { setText(today_entry?.text || ''); }, [today_entry]);

  const handleSave = async () => {
    if (!text.trim()) { toast.error('Write something before saving'); return; }
    setSaving(true);
    try {
      await axios.post(`${API}/integration/preparation/entry`, {
        user_id: userId, protocol_id: protocol.id, day: prep_day, text: text.trim(),
      });
      toast.success("Today's preparation saved");
      onRefresh();
    } catch {
      toast.error('Could not save entry');
    } finally {
      setSaving(false);
    }
  };

  const beginIntegration = async () => {
    setStartingIntegration(true);
    try {
      await axios.post(`${API}/integration/ceremony`, {
        user_id: userId,
        ceremony_date: protocol.ceremony_date,
        ceremony_type: protocol.ceremony_type,
        ceremony_note: protocol.intention || '',
      });
      toast.success('30-day integration protocol started');
      onRefresh();
    } catch {
      toast.error('Could not start integration');
    } finally {
      setStartingIntegration(false);
    }
  };

  // Ceremony has passed — transition screen
  if (phase === 'post_ceremony') {
    return (
      <div style={{ padding: '4px 0 80px', textAlign: 'center' }}>
        <div style={{
          background: 'linear-gradient(160deg, #2A1D3D 0%, #1E4A47 100%)',
          borderRadius: 20, padding: '40px 24px', marginBottom: 20,
        }}>
          <div style={{ fontSize: '2rem', marginBottom: 12, opacity: 0.85 }}>◈</div>
          <h2 style={{ margin: '0 0 10px', color: '#fff', fontFamily: "'Playfair Display', serif", fontWeight: 400, fontSize: '1.4rem' }}>
            The ceremony has passed
          </h2>
          <p style={{ margin: '0 auto', color: 'rgba(255,255,255,0.6)', fontSize: '0.88rem', lineHeight: 1.7, maxWidth: 340 }}>
            Your preparation is complete. The most important window begins now —
            the first 30 days, when the experience settles into your life.
          </p>
        </div>
        <button onClick={beginIntegration} disabled={startingIntegration} style={primaryBtn(startingIntegration)}>
          {startingIntegration ? 'Starting…' : 'Begin 30-day integration →'}
        </button>
      </div>
    );
  }

  return (
    <div style={{ padding: '4px 0 100px' }}>
      {/* Countdown header */}
      <div style={{
        background: 'linear-gradient(135deg, rgba(107,84,139,0.55) 0%, rgba(13,43,42,0.95) 100%)',
        borderRadius: 20, padding: '24px 20px', marginBottom: 16,
        border: '1px solid rgba(107,84,139,0.3)',
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <div>
            <p style={{ margin: '0 0 4px', fontSize: '0.68rem', color: 'rgba(255,255,255,0.5)', fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase' }}>
              Preparation · Day {prep_day} of 14
            </p>
            <p style={{ margin: 0, color: 'rgba(255,255,255,0.75)', fontSize: '0.82rem', lineHeight: 1.5, maxWidth: 240 }}>
              Arriving prepared changes the experience. Intention, safety, and a calm baseline.
            </p>
          </div>
          <div style={{ textAlign: 'right' }}>
            <span style={{ fontSize: '1.5rem', fontWeight: 700, color: '#fff' }}>
              T−{days_until_ceremony}
            </span>
            <p style={{ margin: '2px 0 0', fontSize: '0.7rem', color: 'rgba(255,255,255,0.45)' }}>
              {days_until_ceremony === 0 ? 'ceremony today' : 'days to ceremony'}
            </p>
          </div>
        </div>
        <p style={{ margin: '14px 0 0', fontSize: '0.72rem', color: 'rgba(255,255,255,0.4)' }}>
          {protocol.ceremony_type} · {protocol.ceremony_date}
        </p>
      </div>

      {/* Safety screening */}
      <div style={{
        ...card,
        border: screening_done ? '1px solid rgba(30,107,104,0.25)' : '1px solid rgba(201,152,62,0.4)',
      }}>
        <div
          onClick={() => !screening_done && setShowScreening(s => !s)}
          style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: screening_done ? 'default' : 'pointer' }}
        >
          <div>
            <p style={{ ...sectionLabel, margin: 0, color: screening_done ? '#1E6B68' : '#B08430' }}>
              Safety screening {screening_done ? '· complete' : '· required'}
            </p>
            {!screening_done && (
              <p style={{ margin: '6px 0 0', fontSize: '0.8rem', color: '#5A7370', lineHeight: 1.5 }}>
                14 questions on history and medication. Flags anything that needs medical review before the session.
              </p>
            )}
          </div>
          {!screening_done && <span style={{ color: '#B08430', fontSize: '1.1rem' }}>{showScreening ? '−' : '+'}</span>}
        </div>
        {screening_done && screening?.flags && (
          <div style={{ marginTop: 10 }}>
            {screening.flags.length === 0 ? (
              <p style={{ margin: 0, fontSize: '0.82rem', color: '#1E6B68' }}>✓ No flags raised.</p>
            ) : (
              <p style={{ margin: 0, fontSize: '0.82rem', color: '#B08430', fontWeight: 600 }}>
                {screening.flags.length} item{screening.flags.length > 1 ? 's' : ''} flagged for medical review —
                share with your facilitator and prescriber.
              </p>
            )}
          </div>
        )}
        {!screening_done && showScreening && (
          <div style={{ marginTop: 14 }}>
            <ScreeningForm userId={userId} protocolId={protocol.id} onSaved={onRefresh} />
          </div>
        )}
      </div>

      {/* Baseline */}
      {!baseline_done && (
        <div style={{ ...card, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12 }}>
          <div>
            <p style={{ ...sectionLabel, margin: '0 0 4px' }}>Baseline · recommended</p>
            <p style={{ margin: 0, fontSize: '0.8rem', color: '#5A7370', lineHeight: 1.5 }}>
              Capture your PHQ-9 / GAD-7 before the ceremony — it is what makes your change measurable.
            </p>
          </div>
          <button
            onClick={() => navigate('/assessment')}
            style={{
              flexShrink: 0, padding: '10px 18px', borderRadius: 100, border: 'none', cursor: 'pointer',
              background: 'rgba(30,107,104,0.1)', color: '#1E6B68', fontSize: '0.8rem', fontWeight: 700,
            }}
          >
            Start →
          </button>
        </div>
      )}

      {/* Daily prompt */}
      <div style={card}>
        <p style={sectionLabel}>Preparation prompt · day {prep_day}</p>
        <p style={{
          margin: '0 0 20px', fontSize: '1rem', color: '#1E3A39',
          fontFamily: "'Playfair Display', serif", lineHeight: 1.6,
        }}>
          {daily_prompt}
        </p>
        <textarea
          value={text}
          onChange={e => setText(e.target.value)}
          placeholder="Write freely. This is only for you."
          rows={5}
          style={{
            width: '100%', padding: '12px', boxSizing: 'border-box',
            border: '1.5px solid rgba(30,107,104,0.15)', borderRadius: 12,
            fontSize: '0.9rem', color: '#1E3A39', background: 'rgba(248,252,251,0.8)',
            outline: 'none', resize: 'none', lineHeight: 1.7, fontFamily: 'inherit',
          }}
        />
      </div>

      <button onClick={handleSave} disabled={saving} style={primaryBtn(saving)}>
        {saving ? 'Saving…' : today_done ? "Update today's entry" : "Save today's entry"}
      </button>
    </div>
  );
}

// ── Start Screen — routes to preparation or integration by date ─────────────
function StartProtocol({ userId, onStarted }) {
  const [date, setDate]     = useState(new Date().toISOString().split('T')[0]);
  const [type, setType]     = useState('');
  const [note, setNote]     = useState('');
  const [loading, setLoading] = useState(false);

  const todayStr = new Date().toISOString().split('T')[0];
  const isFuture = date > todayStr;

  const handleStart = async () => {
    if (!type) { toast.error('Please select a ceremony type'); return; }
    setLoading(true);
    try {
      if (isFuture) {
        await axios.post(`${API}/integration/preparation`, {
          user_id: userId,
          ceremony_date: date,
          ceremony_type: type,
          intention: note,
        });
        toast.success('Preparation protocol started');
      } else {
        await axios.post(`${API}/integration/ceremony`, {
          user_id: userId,
          ceremony_date: date,
          ceremony_type: type,
          ceremony_note: note,
        });
        toast.success('30-day integration protocol started');
      }
      onStarted();
    } catch {
      toast.error('Could not start protocol. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ padding: '4px 0 80px' }}>
      {/* Header */}
      <div style={{
        background: 'linear-gradient(160deg, #0D2B2A 0%, #1E4A47 100%)',
        borderRadius: 20, padding: '32px 24px', marginBottom: 20, textAlign: 'center',
      }}>
        <div style={{ fontSize: '2rem', marginBottom: 12, opacity: 0.8 }}>◈</div>
        <h2 style={{
          margin: '0 0 10px', color: '#fff',
          fontFamily: "'Playfair Display', serif", fontWeight: 400, fontSize: '1.4rem',
        }}>
          Integration Studio
        </h2>
        <p style={{ margin: 0, color: 'rgba(255,255,255,0.6)', fontSize: '0.88rem', lineHeight: 1.6 }}>
          The full arc of a psychedelic process: 14 days of preparation, the ceremony,
          and a 30-day structured integration protocol.
        </p>
      </div>

      {/* What to expect */}
      <div style={{ ...card, padding: '20px' }}>
        <p style={{ margin: '0 0 14px', fontSize: '0.85rem', fontWeight: 700, color: '#1E3A39' }}>
          The protocol
        </p>
        {[
          { phase: 'T−14 → T0', name: 'Preparation', desc: 'Intention, safety screening, clinical baseline', color: '#6B548B' },
          { phase: 'Days 1–3', name: 'Landing', desc: 'Silence, body awareness, grounding', color: PHASE_COLORS[0].accent },
          { phase: 'Days 4–7', name: 'Unfolding', desc: 'Observing what emerged without interpretation', color: PHASE_COLORS[1].accent },
          { phase: 'Days 8–14', name: 'Digestion', desc: 'Patterns become visible — write and feel', color: PHASE_COLORS[2].accent },
          { phase: 'Days 15–21', name: 'Integration', desc: 'Connecting insights to daily life', color: PHASE_COLORS[3].accent },
          { phase: 'Days 22–30', name: 'Anchoring', desc: 'Making the learning yours', color: PHASE_COLORS[4].accent },
        ].map((p, i) => (
          <div key={i} style={{ display: 'flex', gap: 12, marginBottom: 10, alignItems: 'flex-start' }}>
            <div style={{ minWidth: 8, height: 8, borderRadius: '50%', background: p.color, marginTop: 6 }} />
            <div>
              <span style={{ fontSize: '0.8rem', color: '#1E6B68', fontWeight: 600 }}>{p.phase} · {p.name}</span>
              <p style={{ margin: '2px 0 0', fontSize: '0.78rem', color: '#8FA3A0' }}>{p.desc}</p>
            </div>
          </div>
        ))}
      </div>

      {/* Form */}
      <div style={{ ...card, padding: '20px', marginBottom: 0 }}>
        <p style={{ margin: '0 0 16px', fontSize: '0.85rem', fontWeight: 700, color: '#1E3A39' }}>
          {isFuture ? 'Prepare for your ceremony' : 'Start your integration'}
        </p>

        <label style={{ display: 'block', marginBottom: 6 }}>
          <span style={{ fontSize: '0.78rem', color: '#8FA3A0', display: 'block', marginBottom: 6 }}>
            Ceremony date
          </span>
          <input
            type="date"
            value={date}
            onChange={e => setDate(e.target.value)}
            style={{
              width: '100%', padding: '10px 12px', boxSizing: 'border-box',
              border: '1.5px solid rgba(30,107,104,0.2)', borderRadius: 10,
              fontSize: '0.9rem', color: '#1E3A39', background: '#fff',
              outline: 'none',
            }}
          />
        </label>
        <p style={{ margin: '0 0 12px', fontSize: '0.72rem', color: isFuture ? '#6B548B' : '#AAB8B6', lineHeight: 1.5 }}>
          {isFuture
            ? '◈ Future date — a preparation protocol begins now, and integration starts after the ceremony.'
            : 'Past or today — the 30-day integration protocol begins immediately.'}
        </p>

        <label style={{ display: 'block', marginBottom: 12 }}>
          <span style={{ fontSize: '0.78rem', color: '#8FA3A0', display: 'block', marginBottom: 6 }}>
            Ceremony type
          </span>
          <select
            value={type}
            onChange={e => setType(e.target.value)}
            style={{
              width: '100%', padding: '10px 12px', boxSizing: 'border-box',
              border: '1.5px solid rgba(30,107,104,0.2)', borderRadius: 10,
              fontSize: '0.9rem', color: type ? '#1E3A39' : '#AAB8B6',
              background: '#fff', outline: 'none', appearance: 'none',
            }}
          >
            <option value="">Select…</option>
            {CEREMONY_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
          </select>
        </label>

        <label style={{ display: 'block', marginBottom: 20 }}>
          <span style={{ fontSize: '0.78rem', color: '#8FA3A0', display: 'block', marginBottom: 6 }}>
            Intention (optional)
          </span>
          <textarea
            value={note}
            onChange={e => setNote(e.target.value)}
            placeholder="What are you seeking or opening to?"
            rows={3}
            style={{
              width: '100%', padding: '10px 12px', boxSizing: 'border-box',
              border: '1.5px solid rgba(30,107,104,0.2)', borderRadius: 10,
              fontSize: '0.88rem', color: '#1E3A39', background: '#fff',
              outline: 'none', resize: 'none', lineHeight: 1.6,
            }}
          />
        </label>

        <button onClick={handleStart} disabled={loading || !type} style={primaryBtn(loading || !type)}>
          {loading ? 'Starting…' : isFuture ? 'Begin preparation →' : 'Begin 30-day protocol →'}
        </button>
      </div>
    </div>
  );
}

// ── Active Protocol Screen (integration T+1 → T+30) ─────────────────────────
function ActiveProtocol({ userId, data, onRefresh }) {
  const { protocol, day_number, phase, phase_index, daily_prompt, today_done, today_entry } = data;
  const colors = PHASE_COLORS[phase_index] || PHASE_COLORS[0];

  const [text, setText]           = useState(today_entry?.text || '');
  const [bodySensation, setBody]  = useState(today_entry?.body_sensation || '');
  const [mood, setMood]           = useState(today_entry?.mood || 3);
  const [saving, setSaving]       = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [entries, setEntries]     = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [showSOS, setShowSOS] = useState(false);
  const [showAssessment, setShowAssessment] = useState(false);
  const [acuteDone, setAcuteDone] = useState(null); // null = checking

  useEffect(() => {
    if (today_entry) {
      setText(today_entry.text || '');
      setBody(today_entry.body_sensation || '');
      setMood(today_entry.mood || 3);
    }
  }, [today_entry]);

  // Has the MEQ-30/EBI been completed for this protocol?
  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API}/assessments/meq30/${encodeURIComponent(userId)}?limit=5`);
        const done = (res.data.assessments || []).some(a => a.protocol_id === protocol.id);
        setAcuteDone(done);
      } catch {
        setAcuteDone(false);
      }
    })();
  }, [userId, protocol.id]);

  const loadHistory = useCallback(async () => {
    setLoadingHistory(true);
    try {
      const res = await axios.get(`${API}/integration/entries/${userId}?protocol_id=${protocol.id}`);
      setEntries(res.data || []);
    } catch {
      toast.error('Could not load history');
    } finally {
      setLoadingHistory(false);
    }
  }, [userId, protocol.id]);

  useEffect(() => {
    if (showHistory) loadHistory();
  }, [showHistory, loadHistory]);

  const handleSave = async () => {
    if (!text.trim()) { toast.error('Write something before saving'); return; }
    setSaving(true);
    try {
      await axios.post(`${API}/integration/entry`, {
        user_id: userId,
        protocol_id: protocol.id,
        day: day_number,
        text: text.trim(),
        body_sensation: bodySensation.trim(),
        mood,
      });
      toast.success("Today's entry saved");
      onRefresh();
    } catch {
      toast.error('Could not save entry');
    } finally {
      setSaving(false);
    }
  };

  const daysRemaining = 30 - day_number;
  const progressPct   = Math.round((day_number / 30) * 100);

  return (
    <div style={{ padding: '4px 0 100px' }}>
      {showSOS && (
        <SOSOverlay userId={userId} protocolId={protocol.id} onClose={() => setShowSOS(false)} />
      )}
      {showAssessment && (
        <AcuteAssessment
          userId={userId}
          protocolId={protocol.id}
          onDone={() => setAcuteDone(true)}
          onClose={() => setShowAssessment(false)}
        />
      )}

      {/* Phase header */}
      <div style={{
        background: `linear-gradient(135deg, ${colors.bg.replace('0.1', '0.6')} 0%, rgba(13,43,42,0.95) 100%)`,
        borderRadius: 20, padding: '24px 20px', marginBottom: 16,
        border: `1px solid ${colors.border}`,
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 12 }}>
          <div>
            <p style={{ margin: '0 0 4px', fontSize: '0.68rem', color: 'rgba(255,255,255,0.5)', fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase' }}>
              Day {day_number} · {phase?.name}
            </p>
            <p style={{ margin: 0, color: 'rgba(255,255,255,0.75)', fontSize: '0.82rem', lineHeight: 1.5, maxWidth: 240 }}>
              {phase?.theme}
            </p>
          </div>
          <div style={{ textAlign: 'right' }}>
            <span style={{ fontSize: '1.4rem', fontWeight: 700, color: '#fff' }}>{progressPct}%</span>
            <p style={{ margin: '2px 0 0', fontSize: '0.7rem', color: 'rgba(255,255,255,0.45)' }}>
              {daysRemaining}d left
            </p>
          </div>
        </div>
        <PhaseBar phaseIndex={phase_index} dayNumber={day_number} />
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <p style={{ margin: 0, fontSize: '0.72rem', color: 'rgba(255,255,255,0.4)' }}>
            {protocol.ceremony_type} · {protocol.ceremony_date}
          </p>
          <button
            onClick={() => setShowSOS(true)}
            style={{
              background: 'rgba(255,255,255,0.12)', border: '1px solid rgba(255,255,255,0.2)',
              color: 'rgba(255,255,255,0.85)', borderRadius: 100, padding: '6px 16px',
              fontSize: '0.75rem', fontWeight: 700, cursor: 'pointer', letterSpacing: '0.04em',
            }}
          >
            ◉ Difficult moment
          </button>
        </div>
      </div>

      {/* Acute experience assessment card (until completed) */}
      {acuteDone === false && (
        <div style={{
          ...card,
          border: '1px solid rgba(107,84,139,0.35)',
          background: 'linear-gradient(135deg, rgba(107,84,139,0.07) 0%, rgba(255,255,255,0.95) 55%)',
        }}>
          <p style={{ ...sectionLabel, color: '#6B548B' }}>
            Acute experience assessment {day_number <= 3 ? '· recommended now' : ''}
          </p>
          <p style={{ margin: '0 0 14px', fontSize: '0.85rem', color: '#1E3A39', lineHeight: 1.6 }}>
            MEQ-30 + EBI — the validated research scales for the experience itself.
            Best completed in the first days after the ceremony, while memory is fresh.
          </p>
          <button
            onClick={() => setShowAssessment(true)}
            style={{
              padding: '11px 22px', borderRadius: 12, border: 'none', cursor: 'pointer',
              background: 'linear-gradient(135deg, #6B548B 0%, #8B7AAA 100%)',
              color: '#fff', fontSize: '0.85rem', fontWeight: 600,
            }}
          >
            Assess my experience →
          </button>
        </div>
      )}

      {/* Today's prompt */}
      <div style={card}>
        <p style={sectionLabel}>Today's prompt</p>
        <p style={{
          margin: '0 0 20px', fontSize: '1rem', color: '#1E3A39',
          fontFamily: "'Playfair Display', serif", lineHeight: 1.6,
        }}>
          {daily_prompt}
        </p>

        <textarea
          value={text}
          onChange={e => setText(e.target.value)}
          placeholder="Write freely. This is only for you."
          rows={6}
          style={{
            width: '100%', padding: '12px', boxSizing: 'border-box',
            border: '1.5px solid rgba(30,107,104,0.15)', borderRadius: 12,
            fontSize: '0.9rem', color: '#1E3A39', background: 'rgba(248,252,251,0.8)',
            outline: 'none', resize: 'none', lineHeight: 1.7,
            fontFamily: 'inherit',
          }}
        />
      </div>

      {/* Body sensation */}
      <div style={card}>
        <p style={sectionLabel}>Body sensation</p>
        <input
          value={bodySensation}
          onChange={e => setBody(e.target.value)}
          placeholder="Tight chest, open shoulders, warmth in hands…"
          style={{
            width: '100%', padding: '10px 12px', boxSizing: 'border-box',
            border: '1.5px solid rgba(30,107,104,0.15)', borderRadius: 10,
            fontSize: '0.88rem', color: '#1E3A39', background: 'rgba(248,252,251,0.8)',
            outline: 'none',
          }}
        />
      </div>

      {/* Mood */}
      <div style={{ ...card, marginBottom: 20 }}>
        <p style={{ ...sectionLabel, marginBottom: 14 }}>How are you today?</p>
        <MoodDots value={mood} onChange={setMood} />
      </div>

      {/* Save */}
      <button onClick={handleSave} disabled={saving} style={{ ...primaryBtn(saving), marginBottom: 14 }}>
        {saving ? 'Saving…' : today_done ? "Update today's entry" : "Save today's entry"}
      </button>

      {/* History toggle */}
      <button
        onClick={() => setShowHistory(!showHistory)}
        style={{
          width: '100%', padding: '12px',
          background: 'transparent',
          border: '1.5px solid rgba(30,107,104,0.2)', borderRadius: 12,
          color: '#1E6B68', fontSize: '0.88rem', fontWeight: 600, cursor: 'pointer',
        }}
      >
        {showHistory ? 'Hide journal entries' : `View journal entries (${data.entries_count || 0})`}
      </button>

      {/* History list */}
      {showHistory && (
        <div style={{ marginTop: 16 }}>
          {loadingHistory ? (
            <p style={{ textAlign: 'center', color: '#AAB8B6', fontSize: '0.85rem' }}>Loading…</p>
          ) : entries.length === 0 ? (
            <p style={{ textAlign: 'center', color: '#AAB8B6', fontSize: '0.85rem' }}>No entries yet.</p>
          ) : (
            entries.map(entry => (
              <div key={entry.id} style={{
                background: 'rgba(255,255,255,0.9)', borderRadius: 14,
                border: '1px solid rgba(184,211,207,0.25)', padding: '16px', marginBottom: 10,
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                  <span style={{ fontSize: '0.78rem', fontWeight: 700, color: '#1E6B68' }}>
                    Day {entry.day}
                  </span>
                  <span style={{ fontSize: '0.72rem', color: '#AAB8B6' }}>
                    {'● '.repeat(entry.mood || 0).trim()}
                  </span>
                </div>
                <p style={{ margin: 0, fontSize: '0.85rem', color: '#1E3A39', lineHeight: 1.6, whiteSpace: 'pre-wrap' }}>
                  {entry.text}
                </p>
                {entry.body_sensation && (
                  <p style={{ margin: '8px 0 0', fontSize: '0.78rem', color: '#8FA3A0', fontStyle: 'italic' }}>
                    Body: {entry.body_sensation}
                  </p>
                )}
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
}

// ── Main component ──────────────────────────────────────────────────────────
export default function IntegrationStudioSection({ user }) {
  const userId = user?.user_id || user?.sub;
  const [loading, setLoading]   = useState(true);
  const [mode, setMode]         = useState('none'); // none | preparation | integration
  const [protocolData, setData] = useState(null);

  const load = useCallback(async () => {
    if (!userId) return;
    try {
      const res = await axios.get(`${API}/integration/ceremony/${encodeURIComponent(userId)}/active`);
      if (res.data?.active) {
        setMode('integration');
        setData(res.data);
        return;
      }
      const prep = await axios.get(`${API}/integration/preparation/${encodeURIComponent(userId)}/active`);
      if (prep.data?.active) {
        setMode('preparation');
        setData(prep.data);
        return;
      }
      setMode('none');
      setData(null);
    } catch {
      setMode('none');
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => { load(); }, [load]);

  if (loading) {
    return (
      <div style={{ padding: '60px 20px', textAlign: 'center' }}>
        <div style={{
          width: 36, height: 36, margin: '0 auto',
          border: '2px solid rgba(30,107,104,0.15)',
          borderTop: '2px solid #1E6B68',
          borderRadius: '50%',
          animation: 'spin 1s linear infinite',
        }} />
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  if (mode === 'integration') {
    return <ActiveProtocol userId={userId} data={protocolData} onRefresh={load} />;
  }
  if (mode === 'preparation') {
    return <ActivePreparation userId={userId} data={protocolData} onRefresh={load} />;
  }
  return <StartProtocol userId={userId} onStarted={load} />;
}
